<template>
  <div class="tw-min-h-screen tw-bg-slate-50 tw-py-10">
    <div class="tw-container tw-mx-auto tw-max-w-7xl tw-px-4">
      <PageHeader :whatsapp-url="whatsappUrl" />

      <LoadingState v-if="loading" />

      <OrderConfirmation
        v-else-if="isConfirmMode && orderData"
        :order="orderData"
        :pending="orderPending"
      />

      <ManualLookup
        v-else-if="showManualLookup"
        @lookup-success="handleLookupSuccess"
        @lookup-error="handleLookupError"
      />

      <!-- Main quote / checkout layout -->
      <div
        v-else-if="quoteData"
        class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8 lg:tw-items-start"
      >
        <!-- Left: quote overview -->
        <section class="lg:tw-col-span-2 tw-space-y-4">
          <QuoteStatusCard
            :reference="quoteData?.quote?.reference"
            :job-number="quoteData?.quote?.job_number"
            :status-label="statusLabel"
            :status-pill-class="statusPillClass"
            :can-checkout="canCheckout"
          />

          <QuoteDetails
            :quote-data="quoteData"
            :editable="false"
            :selected-addons="selectedCardAddons"
            @toggle-addon="handleToggleAddon"
          />
        </section>

        <!-- Right: confirmation & payment column -->
        <aside class="lg:tw-col-span-1 tw-space-y-6 lg:tw-sticky lg:tw-top-6 lg:tw-self-start">
          <CheckoutLockedCard
            v-if="!canCheckout"
            :message="lockedStatusMessage"
          />

          <AddonAmendmentsCard
            v-else-if="hasSelectedCardAddons"
            v-model:requester-name="addonRequesterName"
            v-model:requester-email="addonRequesterEmail"
            :selected-addons-list="selectedCardAddonsList"
            :submitting="addonAmendmentsSubmitting"
            @submit="submitCardAddonAmendments"
            @clear="clearCardAddons"
            @remove-addon="handleRemoveAddon"
          />

          <ReviewConfirmStep
            v-else-if="!specificationConfirmed"
            v-model:confirmation-checked="confirmationChecked"
            v-model:approved-to-sign="approvedToSign"
            v-model:approver-name="approverName"
            v-model:approver-email="approverEmail"
            v-model:preferred-delivery-week="preferredDeliveryWeek"
            :terms-accepted="termsAccepted"
            :available-delivery-weeks="availableDeliveryWeeks"
            :lead-times-url="leadTimesUrl"
            :promo-banner-image="promoBannerImage"
            :promo-banner-alt="promoBannerAlt"
            :can-request-amendments="canRequestAmendments"
            @open-expedite="openExpediteModal"
            @open-amendments="openAmendmentsModal"
            @open-terms="openTermsModal"
            @confirm="confirmSpecification"
          />

          <template v-else>
            <PaymentMethodStep
              v-model:payment-type="paymentType"
              v-model:kerbside-accepted="kerbsideAccepted"
              v-model:vehicle-size-accepted="vehicleSizeAccepted"
              v-model:notes="deliveryNotes"
              :credit-available="creditAvailable && !isZeroTotal && !isFiftyFifty"
              :delivery-accepted="deliveryAccepted"
              @back="backToReview"
              @open-delivery="openDeliveryModal"
            />

            <PendingAcceptanceCard
              v-if="showPendingAcceptance"
              :reference="pendingReference"
              @close="closePendingAcceptance"
            />

            <CheckoutForm
              v-else
              :quote-data="quoteData"
              :payment-method="paymentMethod"
              :payment-type="paymentType"
              :approved-to-sign="approvedToSign"
              :approver-name="approverName"
              :approver-email="approverEmail"
              :preferred-delivery-week="preferredDeliveryWeek"
              :delivery-accepted="deliveryAccepted"
              :kerbside-accepted="kerbsideAccepted"
              :vehicle-size-accepted="vehicleSizeAccepted"
              :notes="deliveryNotes"
              @account-success="handleAccountSuccess"
              @account-error="handleAccountError"
            />
          </template>

          <!-- Voucher Card -->
          <div
            v-if="canCheckout && !hasSelectedCardAddons"
            class="tw-bg-white tw-shadow-md tw-rounded-xl tw-p-6 tw-border tw-border-slate-100"
          >
            <h3 class="tw-text-sm tw-font-semibold tw-text-slate-900 tw-mb-3">
              Voucher Code
            </h3>
            <div
v-if="appliedVoucherCode"
class="tw-flex tw-items-center tw-justify-between tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg tw-px-3 tw-py-2"
>
              <div class="tw-flex tw-items-center tw-gap-2">
                <i class="pi pi-ticket tw-text-emerald-600" />
                <span class="tw-text-xs tw-font-medium tw-text-emerald-800 tw-uppercase">
                  {{ appliedVoucherCode }}
                </span>
              </div>
              <button
                type="button"
                class="tw-text-xs tw-text-red-600 hover:tw-text-red-800 tw-font-medium"
                :disabled="voucherSubmitting"
                @click="handleRemoveVoucher"
              >
                {{ voucherSubmitting ? 'Removing...' : 'Remove' }}
              </button>
            </div>
            <div
v-else
class="tw-flex tw-gap-2"
>
              <input
                v-model="voucherInput"
                type="text"
                placeholder="Enter voucher code"
                class="tw-flex-1 tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-uppercase focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                :disabled="voucherSubmitting"
                @keyup.enter="handleApplyVoucher"
              >
              <button
                type="button"
                class="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium disabled:tw-opacity-50"
                :disabled="!voucherInput.trim() || voucherSubmitting"
                @click="handleApplyVoucher"
              >
                {{ voucherSubmitting ? 'Applying...' : 'Apply' }}
              </button>
            </div>
          </div>

          <PromotionalBanners
            v-if="promotionalBanners.length"
            :banners="promotionalBanners"
          />
        </aside>
      </div>
    </div>

    <SuccessModal
      :show="showSuccessModal"
      :reference="paymentReference"
      @close="closeSuccessModal"
    />

    <ExpediteModal
      v-model:required-date="expediteRequiredDate"
      :show="showExpediteModal"
      :error="expediteError"
      @close="closeExpediteModal"
      @submit="submitExpediteRequest"
    />

    <AmendmentsModal
      v-model:amend-all-selected="amendAllSelected"
      v-model:amend-all-notes="amendAllNotes"
      v-model:requester-name="amendRequesterName"
      v-model:requester-email="amendRequesterEmail"
      :show="showAmendmentsModal"
      :submitting="amendmentsSubmitting"
      :amendment-options="amendmentOptions"
      @close="closeAmendmentsModal"
      @submit="submitAmendments"
    />

    <TermsModal
      :show="showTermsModal"
      :terms-url="termsUrl"
      @close="closeTermsModal"
      @accept="handleTermsAccepted"
    />

    <DeliveryGuidelinesModal
      :show="showDeliveryModal"
      :delivery-url="deliveryUrl"
      @close="closeDeliveryModal"
      @accept="handleDeliveryAccepted"
    />
  </div>
</template>

<script>
import { ref, onMounted, inject, computed, watch, onBeforeUnmount } from 'vue'
import { useToast } from 'vue-toastification'
import PageHeader from './components/PageHeader.vue'
import LoadingState from './components/LoadingState.vue'
import ManualLookup from './components/ManualLookup.vue'
import QuoteStatusCard from './components/QuoteStatusCard.vue'
import QuoteDetails from './components/QuoteDetails.vue'
import CheckoutLockedCard from './components/CheckoutLockedCard.vue'
import ReviewConfirmStep from './components/ReviewConfirmStep.vue'
import PaymentMethodStep from './components/PaymentMethodStep.vue'
import PendingAcceptanceCard from './components/PendingAcceptanceCard.vue'
import CheckoutForm from './components/CheckoutForm.vue'
import SuccessModal from './components/SuccessModal.vue'
import ExpediteModal from './components/ExpediteModal.vue'
import AmendmentsModal from './components/AmendmentsModal.vue'
import TermsModal from './components/TermsModal.vue'
import DeliveryGuidelinesModal from './components/DeliveryGuidelinesModal.vue'
import AddonAmendmentsCard from './components/AddonAmendmentsCard.vue'
import OrderConfirmation from './components/OrderConfirmation.vue'
import PromotionalBanners from './components/PromotionalBanners.vue'

export default {
  name: 'QuotePaymentPage',
  components: {
    PageHeader,
    LoadingState,
    ManualLookup,
    QuoteStatusCard,
    QuoteDetails,
    CheckoutLockedCard,
    ReviewConfirmStep,
    PaymentMethodStep,
    PendingAcceptanceCard,
    CheckoutForm,
    SuccessModal,
    ExpediteModal,
    AmendmentsModal,
    TermsModal,
    DeliveryGuidelinesModal,
    AddonAmendmentsCard,
    OrderConfirmation,
    PromotionalBanners
  },
  setup() {
    const quoteBuilderService = inject('quoteBuilderService')
    const coreService = inject('coreService')
    const toast = useToast()

    // Order confirmation mode
    const isConfirmMode = ref(false)
    const confirmToken = ref('')
    const orderData = ref(null)
    const orderPending = ref(false)
    let confirmPollTimer = null

    // State
    const loading = ref(false)
    const error = ref(false)
    const errorMessage = ref('')
    const showManualLookup = ref(false)
    const quoteData = ref(null)
    const processingPayment = ref(false)
    const showSuccessModal = ref(false)
    const paymentReference = ref('')

    const paymentType = ref('super') // 'super' (default) or 'account'
    const specificationConfirmed = ref(false)
    const confirmationChecked = ref(false)

    // Authorization & Terms (Step 1)
    const approvedToSign = ref(false)
    const approverName = ref('')
    const approverEmail = ref('')
    const termsAccepted = ref(false)
    const showTermsModal = ref(false)
    const deliveryAccepted = ref(false)
    const kerbsideAccepted = ref(false)
    const vehicleSizeAccepted = ref(false)
    const deliveryNotes = ref('')
    const showDeliveryModal = ref(false)
    const preferredDeliveryWeek = ref('')

    // Amendments
    const showAmendmentsModal = ref(false)
    const amendmentsSubmitting = ref(false)
    const amendmentOptions = ref([])
    const amendAllSelected = ref(false)
    const amendAllNotes = ref('')

    const showPendingAcceptance = ref(false)
    const pendingReference = ref('')

    // Amendment requester details
    const amendRequesterName = ref('')
    const amendRequesterEmail = ref('')

    // Card addon selection state
    const selectedCardAddons = ref({})
    const addonRequesterName = ref('')
    const addonRequesterEmail = ref('')
    const addonAmendmentsSubmitting = ref(false)

    // Voucher state definitions
    const voucherInput = ref('')
    const voucherSubmitting = ref(false)
    const appliedVoucherCode = computed(() => {
      return quoteData.value?.quote?.voucher_code || ''
    })

    const hasSelectedCardAddons = computed(() => {
      return Object.keys(selectedCardAddons.value).length > 0
    })

    const selectedCardAddonsList = computed(() => {
      return Object.values(selectedCardAddons.value)
    })

    const handleToggleAddon = ({ itemId, addon, selected, key }) => {
      if (selected) {
        // Find the item name for display
        const items = quoteData.value?.items || []
        const item = items.find(i => i.id === itemId)
        selectedCardAddons.value[key] = {
          key,
          itemId,
          addonId: addon.id || addon.code,
          label: addon.label || addon.title,
          subtitle: addon.subtitle || addon.description || '',
          price: parseFloat(addon.price) || 0,
          itemName: item?.name || ''
        }
      } else {
        delete selectedCardAddons.value[key]
      }
      // Trigger reactivity
      selectedCardAddons.value = { ...selectedCardAddons.value }
    }

    const handleRemoveAddon = (addon) => {
      delete selectedCardAddons.value[addon.key]
      selectedCardAddons.value = { ...selectedCardAddons.value }
    }

    const clearCardAddons = () => {
      selectedCardAddons.value = {}
    }

    const submitCardAddonAmendments = async () => {
      if (!quoteBuilderService) {
        toast.error('quoteBuilderService not available')
        return
      }

      if (!addonRequesterName.value.trim()) {
        toast.error('Please enter your name so we know who to contact about the amendments.')
        return
      }
      if (!addonRequesterEmail.value.trim()) {
        toast.error('Please enter your email so we can get back to you about the amendments.')
        return
      }

      const addons = selectedCardAddonsList.value
      if (!addons.length) {
        toast.error('Please select at least one add-on.')
        return
      }

      const token = quoteData.value?.quote?.token
      const quoteId = quoteData.value?.quote?.id

      if (!token || !quoteId) {
        toast.error('Unable to submit amendment request (missing quote token/id).')
        return
      }

      const amendmentsToSubmit = addons.map(a => ({
        type: 'automatic',
        code: a.addonId,
        item_id: a.itemId,
        price_delta: a.price,
        title: a.label,
        notes: ''
      }))

      addonAmendmentsSubmitting.value = true
      try {
        const payload = {
          quote_id: quoteId,
          amendments: amendmentsToSubmit,
          requester_name: addonRequesterName.value.trim(),
          requester_email: addonRequesterEmail.value.trim()
        }

        await quoteBuilderService.requestQuoteAmendments(token, payload)

        toast.success('Amendment request sent. We\'ll be in touch shortly.')
        clearCardAddons()
        addonRequesterName.value = ''
        addonRequesterEmail.value = ''
      } catch (err) {
        console.error('Failed to request amendments:', err)
        toast.error(err?.response?.data?.message || 'Failed to submit amendment request. Please try again.')
      } finally {
        addonAmendmentsSubmitting.value = false
      }
    }

    // Lead times helper state
    const showExpediteModal = ref(false)
    const expediteRequiredDate = ref('')
    const expediteError = ref('')

    const quoteStatus = computed(() => {
      const raw = quoteData.value?.quote?.status
      const num = typeof raw === 'string' ? parseInt(raw, 10) : raw
      return Number.isFinite(num) ? num : 1
    })

    const statusLabel = computed(() => {
      switch (quoteStatus.value) {
        case 1:
          return 'Awaiting sign-off'
        case 2:
          return 'Awaiting amendments'
        case 3:
          return 'Awaiting confirmation'
        default:
          return 'Quote status unknown'
      }
    })

    const statusPillClass = computed(() => {
      switch (quoteStatus.value) {
        case 1:
          return 'tw-bg-emerald-50 tw-text-emerald-800'
        case 2:
          return 'tw-bg-amber-50 tw-text-amber-800'
        case 3:
          return 'tw-bg-sky-50 tw-text-sky-800'
        default:
          return 'tw-bg-slate-100 tw-text-slate-600'
      }
    })

    const canCheckout = computed(() => quoteStatus.value === 1)

    const canRequestAmendments = computed(() => quoteStatus.value === 1)

    const lockedStatusMessage = computed(() => {
      if (quoteStatus.value === 2) {
        return 'This quote is awaiting amendments. Once our team has reviewed your requested changes, we’ll update the quote and re-open checkout.'
      }
      if (quoteStatus.value === 3) {
        return 'This quote is awaiting internal confirmation. We’ll notify you as soon as it is ready for checkout.'
      }
      return 'This quote cannot currently be checked out. Please contact our team for more information.'
    })

    const creditAvailable = computed(() => {
      const raw = quoteData.value?.quote?.credit_available
      return raw === true || raw === 1 || raw === '1'
    })

    const isFiftyFifty = computed(() => {
      const pmId = quoteData.value?.quote?.payment_method_id
      return pmId === 4 || pmId === 5 || pmId === '4' || pmId === '5'
    })

    const isZeroTotal = computed(() => {
      const total = parseFloat(quoteData.value?.quote?.total || quoteData.value?.total || 0)
      const discount = parseFloat(quoteData.value?.quote?.voucher_discount || 0)
      return Math.max(0, total - discount) === 0
    })

    const paymentMethod = computed(() => {
      // Keep existing "2" for Super Payments.
      // For on-account, we send a distinct marker (backend may expect a numeric ID instead).
      if (isZeroTotal.value) return 'account'
      if (paymentType.value === 'bacs') return 'bacs'
      return paymentType.value === 'account' ? 'account' : '2'
    })

    // WhatsApp support button
    const whatsappNumber = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.whatsappNumber)
        ? backendConfig.pageData.whatsappNumber
        : ''

      const fromEnv = process.env.VUE_APP_WHATSAPP_NUMBER || ''
      const raw = String(fromBackendConfig || fromEnv || '')
      // wa.me expects an international number without +, spaces or punctuation
      return raw.replace(/[^\d]/g, '')
    })

    const whatsappMessage = computed(() => {
      const ref = quoteData.value?.quote?.reference
      return ref
        ? `Hi, I need help with my quote (ref: ${ref}).`
        : 'Hi, I need help with quote.'
    })

    const whatsappUrl = computed(() => {
      if (!whatsappNumber.value) return ''
      const text = encodeURIComponent(whatsappMessage.value)
      return `https://wa.me/${whatsappNumber.value}?text=${text}`
    })

    const leadTimesUrl = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.leadTimesUrl)
        ? backendConfig.pageData.leadTimesUrl
        : ''
      return fromBackendConfig || process.env.VUE_APP_LEAD_TIMES_URL || ''
    })

    const promoBannerImage = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.promoBannerImage)
        ? backendConfig.pageData.promoBannerImage
        : ''
      return fromBackendConfig || ''
    })

    const promoBannerAlt = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.promoBannerAlt)
        ? backendConfig.pageData.promoBannerAlt
        : ''
      return fromBackendConfig || 'Special offer'
    })

    const promotionalBanners = computed(() => {
      const banners = quoteData.value?.promotional_banners
      return Array.isArray(banners) ? banners.filter(Boolean) : []
    })

    const termsUrl = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.termsAndConditionsUrl)
        ? backendConfig.pageData.termsAndConditionsUrl
        : ''
      return fromBackendConfig || process.env.VUE_APP_TERMS_URL || 'https://www.quickslide.co.uk/terms-conditions/'
    })

    const deliveryUrl = computed(() => {
      const fromBackendConfig = (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.deliveryGuidelinesUrl)
        ? backendConfig.pageData.deliveryGuidelinesUrl
        : ''
      return fromBackendConfig || process.env.VUE_APP_DELIVERY_URL || ''
    })

    // Helper functions for delivery week calculation
    const toIsoDate = (date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    const addDays = (date, days) => {
      const d = new Date(date)
      d.setDate(d.getDate() + days)
      return d
    }

    const nextMondayOnOrAfter = (date) => {
      const d = new Date(date)
      const day = d.getDay() // 0=Sun,1=Mon,...
      const diff = (8 - day) % 7
      return addDays(d, diff)
    }

    const parseIsoDate = (value) => {
      if (!value || typeof value !== 'string') return null
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!match) return null
      const [, y, m, d] = match
      const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10))
      return Number.isNaN(date.getTime()) ? null : date
    }

    const availableDeliveryWeeks = computed(() => {
      const earliestFromApi = parseIsoDate(quoteData.value?.earliest_delivery_date)

      let firstMonday
      if (earliestFromApi) {
        // Use the API-provided earliest date, snapping forward to a Monday if needed.
        firstMonday = nextMondayOnOrAfter(earliestFromApi)
      } else {
        const today = new Date()
        const min = addDays(today, 14)
        firstMonday = nextMondayOnOrAfter(min)
      }

      const weeksToShow = 20
      const opts = []

      for (let i = 0; i < weeksToShow; i++) {
        const monday = addDays(firstMonday, i * 7)
        const value = toIsoDate(monday)
        const labelDate = monday.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })

        opts.push({
          value,
          label: `Week commencing ${labelDate}`
        })
      }

      return opts
    })

    // Set default delivery week on mount, and re-default when the available weeks
    // change (e.g. once the API quote arrives with earliest_delivery_date).
    watch(availableDeliveryWeeks, (weeks) => {
      if (!weeks.length) return
      const currentIsValid = weeks.some((w) => w.value === preferredDeliveryWeek.value)
      if (!preferredDeliveryWeek.value || !currentIsValid) {
        preferredDeliveryWeek.value = weeks[0].value
      }
    }, { immediate: true })

    // Terms modal functions
    const openTermsModal = () => {
      showTermsModal.value = true
    }

    const closeTermsModal = () => {
      showTermsModal.value = false
    }

    const handleTermsAccepted = () => {
      termsAccepted.value = true
    }

    // Delivery guidelines modal functions
    const openDeliveryModal = () => {
      showDeliveryModal.value = true
    }

    const closeDeliveryModal = () => {
      showDeliveryModal.value = false
    }

    const handleDeliveryAccepted = () => {
      deliveryAccepted.value = true
    }

    // Fetch order by confirmation token
    const fetchOrderConfirmation = async (token) => {
      loading.value = true
      try {
        const response = await coreService.confirmOrder(token)
        const order = response?.data?.data || response?.data

        if (!order) throw new Error('No order data returned')

        orderData.value = order
        orderPending.value = !!order.pending

        if (orderPending.value) {
          startConfirmPolling(token)
        }
      } catch (err) {
        console.error('Order confirmation lookup failed:', err)
        toast.error('Unable to load order details. The link may be invalid or expired.')
      } finally {
        loading.value = false
      }
    }

    const startConfirmPolling = (token) => {
      stopConfirmPolling()
      confirmPollTimer = setInterval(async () => {
        try {
          const response = await coreService.confirmOrder(token)
          const order = response?.data?.data || response?.data

          if (order && !order.pending) {
            orderData.value = order
            orderPending.value = false
            stopConfirmPolling()
          }
        } catch (err) {
          console.error('Confirm poll error:', err)
        }
      }, 5000)
    }

    const stopConfirmPolling = () => {
      if (confirmPollTimer) {
        clearInterval(confirmPollTimer)
        confirmPollTimer = null
      }
    }

    // Check for token on mount
    onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const type = urlParams.get('type')
      const token = urlParams.get('token')

      if (type === 'confirm' && token) {
        isConfirmMode.value = true
        confirmToken.value = token
        await fetchOrderConfirmation(token)
      } else if (token) {
        await lookupQuoteByToken(token)
      } else {
        showManualLookup.value = true
      }
    })

    // Lookup quote by token
    const lookupQuoteByToken = async (token) => {
      loading.value = true
      error.value = false

      try {
        const response = await quoteBuilderService.getQuoteByToken(token)


        if (response && response.data) {
          quoteData.value = response.data
          showManualLookup.value = false
        } else {
          throw new Error('Invalid quote token')
        }
      } catch (err) {
        console.error('Quote lookup failed:', err)
        // Automatically redirect to manual lookup on error
        showManualLookup.value = true
        // Optionally show a toast notification
        toast.error('Quote not found. Please enter your details manually.')
      } finally {
        loading.value = false
      }
    }

    watch([creditAvailable, isFiftyFifty], ([isAvailable, fiftyFifty]) => {
      if (fiftyFifty) {
        paymentType.value = 'super'
      } else if (!isAvailable && paymentType.value === 'account') {
        paymentType.value = 'super'
      }
    }, { immediate: true })

    // Get available addons for an item
    // TODO: Replace with item.addons when API returns them
    const getItemAddons = (item) => {
      const name = (item?.name || '').toLowerCase()
      const addons = []

      return item?.promotions || [];
    }

    const buildAmendmentOptions = () => {
      const items = quoteData.value?.items || []

      const options = items.map((item) => {
        const quantity = parseInt(item.quantity || item.qty || 1)
        const basePrice = parseFloat(item.price) || 0
        const discountAmount = parseFloat(item.discount_amount) || 0
        const unitPrice = basePrice + discountAmount
        const lineTotal = unitPrice * quantity

        // Get addons from API when available, otherwise use fallback
        const availableAddons = item.addons || item.available_addons || getItemAddons(item)

        // Map addons to autoAmendments format
        const autoAmendments = availableAddons.map((addon) => ({
          key: `addon-${addon.id}`,
          type: 'automatic',
          code: addon.id || addon.code,
          itemId: item.id,
          title: addon.label || addon.title,
          subtitle: addon.subtitle || addon.description || '',
          priceDelta: parseFloat(addon.price) || 0,
          placeholder: 'Any notes for this change? (optional)',
          selected: false,
          notes: ''
        }))

        // Always add a custom option at the end
        autoAmendments.push({
          key: `auto-${item.id}-custom`,
          type: 'automatic',
          code: 'CUSTOM',
          itemId: item.id,
          title: 'Custom',
          subtitle: 'Describe the change you want for this item.',
          priceDelta: 0,
          placeholder: 'Any extra detail? (optional)',
          selected: false,
          customTitle: '',
          notes: ''
        })

        return {
          key: `item-${item.id}`,
          type: 'item',
          itemId: item.id,
          title: item.name || `Item #${item.id}`,
          subtitle: quantity ? `Qty ${quantity}` : '',
          image: item.image || '',
          descriptionHtml: item.description || '',
          descriptionExpanded: false,
          unitPrice,
          lineTotal,
          autoAmendments,
          placeholder: 'Tell us what needs changing for this item…',
          selected: false,
          notes: ''
        }
      })

      amendmentOptions.value = options
    }

    watch(quoteData, (data) => {
      if (data) buildAmendmentOptions()
    }, { immediate: true })

    const openAmendmentsModal = () => {
      if (!quoteData.value) return
      if (!amendmentOptions.value?.length) buildAmendmentOptions()
      showAmendmentsModal.value = true
      document.body.style.overflow = 'hidden'
    }

    const closeAmendmentsModal = () => {
      showAmendmentsModal.value = false
      document.body.style.overflow = ''
    }

    const submitAmendments = async () => {
      if (!quoteBuilderService) {
        toast.error('quoteBuilderService not available')
        return
      }

      const items = amendmentOptions.value || []

      // Validate requester details
      if (!amendRequesterName.value.trim()) {
        toast.error('Please enter your name so we know who to contact about the amendments.')
        return
      }
      if (!amendRequesterEmail.value.trim()) {
        toast.error('Please enter your email so we can get back to you about the amendments.')
        return
      }

      // If amend-all is selected, make sure they’ve actually described the change
      if (amendAllSelected.value && !amendAllNotes.value.trim()) {
        toast.error('Please describe the change you want to apply to all items.')
        return
      }

      const selectedAutoAmendments = items.flatMap((a) => {
        const opts = a?.autoAmendments || []
        return opts
          .filter((opt) => opt.selected)
          .map((opt) => ({
            type: 'automatic',
            code: opt.code || null,
            item_id: opt.itemId || a.itemId || null,
            price_delta: opt.priceDelta || 0,
            title: opt.code === 'CUSTOM' ? (opt.customTitle || 'Custom') : (opt.title || null),
            notes: opt.notes || ''
          }))
      })

      const missingCustomTitle = selectedAutoAmendments.some((a) => a.code === 'CUSTOM' && (!a.title || a.title === 'Custom'))
      if (missingCustomTitle) {
        toast.error('Please enter what change you want for the Custom option.')
        return
      }

      const amendmentsToSubmit = []

      if (amendAllSelected.value && amendAllNotes.value.trim()) {
        amendmentsToSubmit.push({
          type: 'global',
          code: 'AMEND_ALL',
          item_id: null,
          price_delta: 0,
          title: 'Changes for all items',
          notes: amendAllNotes.value.trim()
        })
      }

      amendmentsToSubmit.push(...selectedAutoAmendments)

      if (!amendmentsToSubmit.length) {
        toast.error('Please select at least one change.')
        return
      }

      const token = quoteData.value?.quote?.token
      const quoteId = quoteData.value?.quote?.id

      if (!token || !quoteId) {
        toast.error('Unable to submit amendment request (missing quote token/id).')
        return
      }

      amendmentsSubmitting.value = true
      try {
        const payload = {
          quote_id: quoteId,
          amendments: amendmentsToSubmit,
          requester_name: amendRequesterName.value.trim(),
          requester_email: amendRequesterEmail.value.trim()
        }

        await quoteBuilderService.requestQuoteAmendments(token, payload)

        toast.success('Amendment request sent. We’ll be in touch shortly.')
        closeAmendmentsModal()

        // Reset selections after a successful submit
        amendAllSelected.value = false
        amendAllNotes.value = ''
        buildAmendmentOptions()
      } catch (err) {
        console.error('Failed to request amendments:', err)
        toast.error(err?.response?.data?.message || 'Failed to submit amendment request. Please try again.')
      } finally {
        amendmentsSubmitting.value = false
      }
    }

    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        if (showAmendmentsModal.value) closeAmendmentsModal()
        if (showExpediteModal.value) closeExpediteModal()
      }
    }

    onMounted(() => window.addEventListener('keydown', handleKeydown))
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
      stopConfirmPolling()
    })

    // Handle manual lookup success
    const handleLookupSuccess = (data) => {
      quoteData.value = data
      showManualLookup.value = false
      error.value = false
    }

    // Handle manual lookup error
    const handleLookupError = (message) => {
      toast.error(message || 'Failed to lookup quote')
    }

    // Reset to manual lookup
    const resetToManualLookup = () => {
      error.value = false
      errorMessage.value = ''
      showManualLookup.value = true
      quoteData.value = null
    }

    // Handle payment submission
    const handlePaymentSubmit = async (paymentData) => {
      processingPayment.value = true

      try {
        const response = await quoteBuilderService.processQuotePayment({
          quoteId: quoteData.value.id,
          ...paymentData
        })

        if (response && response.data) {
          paymentReference.value = response.data.reference || response.data.order_id
          showSuccessModal.value = true
          toast.success('Payment processed successfully!')
        } else {
          throw new Error('Payment processing failed')
        }
      } catch (err) {
        console.error('Payment failed:', err)
        toast.error(err.response?.data?.message || 'Payment processing failed. Please try again.')
      } finally {
        processingPayment.value = false
      }
    }

    // Handle payment cancellation
    const handlePaymentCancel = () => {
      if (confirm('Are you sure you want to cancel? You can return to complete payment later.')) {
        resetToManualLookup()
      }
    }

    // Lead times / expedite helpers
    const openExpediteModal = () => {
      expediteRequiredDate.value = ''
      expediteError.value = ''
      showExpediteModal.value = true
      document.body.style.overflow = 'hidden'
    }

    const closeExpediteModal = () => {
      showExpediteModal.value = false
      document.body.style.overflow = ''
    }

    const submitExpediteRequest = () => {
      expediteError.value = ''
      if (!expediteRequiredDate.value) {
        expediteError.value = 'Please choose the date you need this order by.'
        return
      }
      if (!whatsappNumber.value) {
        toast.error('WhatsApp number is not configured.')
        return
      }

      const ref = quoteData.value?.quote?.reference || 'N/A'
      const name = quoteData.value?.quote?.customer_name || ''
      const date = expediteRequiredDate.value

      const message = [
        'Hi, I need this quote sooner than the standard lead times.',
        ref ? `Quote ref: ${ref}.` : '',
        name ? `Name: ${name}.` : '',
        `Required by: ${date}.`
      ].filter(Boolean).join(' ')

      const text = encodeURIComponent(message)
      const url = `https://wa.me/${whatsappNumber.value}?text=${text}`
      closeExpediteModal()
      window.location.href = url
    }

    // Close success modal
    const closeSuccessModal = () => {
      showSuccessModal.value = false
      // Optionally redirect or reset
      resetToManualLookup()
    }

    const handleAccountSuccess = (data) => {
      // We expect the backend to return some kind of reference/order id.
      pendingReference.value = data?.reference || data?.order_id || data?.orderId || ''
      showPendingAcceptance.value = true
      toast.success('Added to account. Pending acceptance.')
    }

    const handleAccountError = (message) => {
      toast.error(message || 'Failed to add to account')
    }

    const closePendingAcceptance = () => {
      showPendingAcceptance.value = false
      resetToManualLookup()
    }

    // Single payment method is used; no selector required

    // Confirm specification
    const confirmSpecification = () => {
      if (confirmationChecked.value) {
        specificationConfirmed.value = true
      }
    }

    // Back to review
    const backToReview = () => {
      specificationConfirmed.value = false
      confirmationChecked.value = false
    }

    const handleApplyVoucher = async () => {
      if (!voucherInput.value.trim()) return
      voucherSubmitting.value = true
      try {
        const token = quoteData.value?.quote?.token
        const response = await quoteBuilderService.applyVoucher(token, voucherInput.value.trim())
        quoteData.value = response.data
        toast.success(response.data.message || 'Voucher applied successfully!')
        voucherInput.value = ''
      } catch (err) {
        console.error('Failed to apply voucher:', err)
        toast.error(err?.response?.data?.message || 'Failed to apply voucher. Please try again.')
      } finally {
        voucherSubmitting.value = false
      }
    }

    const handleRemoveVoucher = async () => {
      voucherSubmitting.value = true
      try {
        const token = quoteData.value?.quote?.token
        const response = await quoteBuilderService.removeVoucher(token)
        quoteData.value = response.data
        toast.success(response.data.message || 'Voucher removed successfully!')
      } catch (err) {
        console.error('Failed to remove voucher:', err)
        toast.error(err?.response?.data?.message || 'Failed to remove voucher. Please try again.')
      } finally {
        voucherSubmitting.value = false
      }
    }

    return {
      loading,
      error,
      errorMessage,
      showManualLookup,
      quoteData,
      processingPayment,
      showSuccessModal,
      paymentReference,
      paymentMethod,
      quoteStatus,
      statusLabel,
      statusPillClass,
      canCheckout,
      canRequestAmendments,
      lockedStatusMessage,
      paymentType,
      creditAvailable,
      isFiftyFifty,
      isZeroTotal,
      showPendingAcceptance,
      pendingReference,
      lookupQuoteByToken,
      handleLookupSuccess,
      handleLookupError,
      resetToManualLookup,
      handlePaymentSubmit,
      handlePaymentCancel,
      closeSuccessModal,
      specificationConfirmed,
      confirmationChecked,
      approvedToSign,
      approverName,
      approverEmail,
      termsAccepted,
      showTermsModal,
      termsUrl,
      openTermsModal,
      closeTermsModal,
      handleTermsAccepted,
      deliveryAccepted,
      kerbsideAccepted,
      vehicleSizeAccepted,
      deliveryNotes,
      showDeliveryModal,
      deliveryUrl,
      openDeliveryModal,
      closeDeliveryModal,
      handleDeliveryAccepted,
      preferredDeliveryWeek,
      availableDeliveryWeeks,
      confirmSpecification,
      backToReview,
      whatsappUrl,
      leadTimesUrl,
      promoBannerImage,
      promoBannerAlt,
      promotionalBanners,
      showExpediteModal,
      amendRequesterName,
      amendRequesterEmail,
      expediteRequiredDate,
      expediteError,
      openExpediteModal,
      closeExpediteModal,
      submitExpediteRequest,
      handleAccountSuccess,
      handleAccountError,
      closePendingAcceptance,
      showAmendmentsModal,
      amendmentOptions,
      amendmentsSubmitting,
      amendAllSelected,
      amendAllNotes,
      openAmendmentsModal,
      closeAmendmentsModal,
      submitAmendments,
      selectedCardAddons,
      hasSelectedCardAddons,
      selectedCardAddonsList,
      addonRequesterName,
      addonRequesterEmail,
      addonAmendmentsSubmitting,
      handleToggleAddon,
      handleRemoveAddon,
      clearCardAddons,
      submitCardAddonAmendments,
      isConfirmMode,
      orderData,
      orderPending,
      voucherInput,
      voucherSubmitting,
      appliedVoucherCode,
      handleApplyVoucher,
      handleRemoveVoucher
    }
  }
}
</script>

<style scoped></style>
